import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  TextField,
  DialogActions,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Collapse,
  Autocomplete,
  AppBar,
  Toolbar,
  Paper,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { OrderContext } from "../../contexts/OrderContext";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Add, Close, Remove } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const ITEMS_PER_PAGE = 60;

const StockDataPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { storeData } = useContext(OrderContext);
  const {
    itemtypes = [],
    designs = [],
    purities = [],
    stores = [],
  } = storeData;
  const storeName = stores?.[0]?.name || "";
  const storeLogo = stores?.[0]?.logo || "";

  const { filterId } = useParams();
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
    phone: "",
    deliveryDate: "",
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false); // Track email sending state
  const [isExhibitionOrder, setIsExhibitionOrder] = useState(0);
  const [exhibitionName, setExhibitionName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [prefix, setPrefix] = useState("");
  const [mail, setMail] = useState("");
  const [store_id, setStoreid] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filteredStockArr, setFilteredStockArr] = useState([]);
  const [storeInformation, setStoreInformation] = useState({
    storeLogo: "",
    storeName: "",
    storePlace: "",
    storePrefix: "",
  });
  const [filters, setFilters] = useState({
    itemType: null,
    design: null,
    grossWtMin: "",
    grossWtMax: "",
    netWtMin: "",
    netWtMax: "",
  });
  const [cartItemFormData, setCartItemFormData] = useState({});
  const [emailSuccessOpen, setEmailSuccessOpen] = useState(false);

  const loadingMessages = [
    "Your mail is on its way...",
    "Uhhh... You've placed quite a remarkable order!...",
    "We're almost done. Just a few seconds left...",
    "Don't worry, we didn't freeze. Just being careful...",
  ];
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(
    loadingMessages[0]
  );

  // Showing multiple messages for better UX
  useEffect(() => {
    let intervalId;

    if (isSendingEmail) {
      let index = 0;
      setCurrentLoadingMessage(loadingMessages[0]);

      intervalId = setInterval(() => {
        index += 1;
        if (index < loadingMessages.length) {
          setCurrentLoadingMessage(loadingMessages[index]);
        } else {
          clearInterval(intervalId); // Stop at last message
        }
      }, 5000); // Change message every 3s
    }

    return () => clearInterval(intervalId); // Cleanup on unmount or send complete
  }, [isSendingEmail]);

  const fetchStockData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fetchorder/getStockByFilter/${filterId}`
      );

      setIsExhibitionOrder(response.data.isExhibitionOrder);
      setExhibitionName(response.data.exhibitionName);
      const fetchedStock = response.data.stockData || [];

      setPrefix(response.data.storeInfo[0]?.url_prefix);
      setMail(response.data.storeInfo[0]?.mail_id);
      setStoreid(response.data.storeInfo[0]?.store_id);
      setStoreInformation((prev) => ({
        ...prev,
        storeLogo: response.data.storedetails[0]?.logo,
        storeName: response.data.storedetails[0]?.name,
        storeplace: response.data.storedetails[0]?.place,
        storePrefix: response.data.storedetails[0]?.prefix,
      }));

      setStockData(fetchedStock);

      // ✅ If isExhibitionOrder is true, add all stocks to cart
      if (response.data.isExhibitionOrder === 1) {
        setCart(fetchedStock);
      }
      setLoading(false);
    } catch (error) {
      setError("Failed to load stock data.");
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchStockData();
  }, [filterId]);

  let IMAGE_BASE_URL = `https://kumuduorderapp.blob.core.windows.net/${prefix}/`;

  // ------------------------------------------
  const paginatedStock = useMemo(() => {
    const dataToPaginate = isFilterActive ? filteredStockArr : stockData;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return dataToPaginate.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [stockData, filteredStockArr, currentPage, isFilterActive]);

  const totalPages = Math.ceil(
    (isFilterActive ? filteredStockArr.length : stockData.length) /
      ITEMS_PER_PAGE
  );

  const itemTypeOptions = useMemo(() => {
    const map = new Map(); // key = id, value = { id,name }
    stockData.forEach((s) => {
      if (s.itemtype && s.itemtype_name) {
        map.set(s.itemtype, { id: s.itemtype, name: s.itemtype_name });
      }
    });
    return [...map.values()]; // de‑duplicated, [{id,name}, …]
  }, [stockData]);

  const designOptions = useMemo(() => {
    if (!filters.itemType) return [];
    const map = new Map(); // key = id, value = { id,name }
    stockData
      .filter((s) => s.itemtype === filters.itemType)
      .forEach((s) => {
        if (s.design && s.design_name) {
          map.set(s.design, { id: s.design, name: s.design_name });
        }
      });
    return [...map.values()];
  }, [stockData, filters.itemType]);

  const addToCart = (item) => {
    if (!cart.some((cartItem) => cartItem.tagno === item.tagno)) {
      setCart((prevCart) => [...prevCart, item]);
    }
  };

  const removeFromCart = (tagno) => {
    setCart((prevCart) => prevCart.filter((item) => item.tagno !== tagno));
    setCartItemFormData((prevFormData) => {
      const updated = { ...prevFormData };
      delete updated[tagno];
      return updated;
    });
  };

  const getTotalNetWeight = () => {
    return cart
      .reduce((total, item) => total + parseFloat(item.netwt), 0)
      .toFixed(3);
  };

  const getTotalGrossWeight = () => {
    return cart
      .reduce((total, item) => total + parseFloat(item.gross), 0)
      .toFixed(3);
  };

  const handleSendEmail = async () => {
    setCurrentLoadingMessage(loadingMessages[0]);

    try {
      if (
        !customerDetails.name ||
        !customerDetails.phone ||
        !customerDetails.deliveryDate
      ) {
        alert("Name, Mobile Number, and Delivery Date are required!");
        return;
      }

      setIsSendingEmail(true); // ✅ Start loader

      const emailData = {
        filterId,
        customer: customerDetails,
        cartItems: cart,
        mailid: mail,
        store_id: store_id,
        store_name: storeName,
        store_logo: storeLogo,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/sendmail`,
        emailData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log("Email sent successfully!");
        setIsCheckoutOpen(false);
        setCart([]);
        setCartItemFormData({});
        setEmailSuccessOpen(true); // ✅ Show success dialog
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email. Please try again.");
    } finally {
      setIsSendingEmail(false); // ✅ Stop loader in all cases
    }
  };

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDropdownChange = (name, newValue) => {
    setFilters((prev) => ({ ...prev, [name]: newValue ? newValue.id : "" }));
  };

  const shouldFilterBeEnabled = () => {
    const checkFields = [
      "itemType",
      "design",
      "grossWtMin",
      "grossWtMax",
      "netWtMin",
      "netWtMax",
    ];

    const isAnyFieldFilled = checkFields.some((field) => {
      const fieldValue = filters[field];
      return fieldValue !== "" && fieldValue !== null;
    });

    return isAnyFieldFilled;
  };

  const handleFilter = () => {
    let filtered = [...stockData]; // ✅ Always start from the full stockData

    if (filters.itemType) {
      filtered = filtered.filter((item) => item.itemtype === filters.itemType);
    }

    if (filters.design) {
      filtered = filtered.filter((item) => item.design === filters.design);
    }

    if (filters.grossWtMin !== "") {
      filtered = filtered.filter(
        (item) => parseFloat(item.gross) >= parseFloat(filters.grossWtMin)
      );
    }

    if (filters.grossWtMax !== "") {
      filtered = filtered.filter(
        (item) => parseFloat(item.gross) <= parseFloat(filters.grossWtMax)
      );
    }

    if (filters.netWtMin !== "") {
      filtered = filtered.filter(
        (item) => parseFloat(item.netwt) >= parseFloat(filters.netWtMin)
      );
    }

    if (filters.netWtMax !== "") {
      filtered = filtered.filter(
        (item) => parseFloat(item.netwt) <= parseFloat(filters.netWtMax)
      );
    }

    setFilteredStockArr(filtered);
    setCurrentPage(1);
    setIsFilterActive(true);
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      itemType: "",
      design: "",
      grossWtMin: "",
      grossWtMax: "",
      netWtMin: "",
      netWtMax: "",
      from_date: "",
      to_date: "",
    }));

    setIsFilterActive(false);
  };

  const mapOptions = (data = []) =>
    data.map((item) => ({ id: item.id, name: item.name }));

  const handleFieldChange = (tagno, fieldOrUpdates, value) => {
    setCartItemFormData((prevState) => {
      const prevData = prevState[tagno] || {};
      const newData =
        typeof fieldOrUpdates === "string"
          ? { ...prevData, [fieldOrUpdates]: value }
          : { ...prevData, ...fieldOrUpdates };

      return {
        ...prevState,
        [tagno]: newData,
      };
    });
  };

  const handleItemIncrement = (tagno) => {
    setCartItemFormData((prevState) => {
      const prevItem = prevState[tagno] || {};
      return {
        ...prevState,
        [tagno]: {
          ...prevItem,
          selectedItemPcs: (prevItem.selectedItemPcs || 1) + 1,
        },
      };
    });
  };

  const handleItemDecrement = (tagno) => {
    setCartItemFormData((prevState) => {
      const prevItem = prevState[tagno] || {};
      const currentPcs = prevItem.selectedItemPcs || 1;
      return {
        ...prevState,
        [tagno]: {
          ...prevItem,
          selectedItemPcs: currentPcs > 1 ? currentPcs - 1 : 1,
        },
      };
    });
  };

  const [expandedTagno, setExpandedTagno] = useState(null);

  const handleExpandCartEdit = (tagno) => {
    setExpandedTagno((prev) => (prev === tagno ? null : tagno));

    // Initialize form if not present
    if (!cartItemFormData[tagno]) {
      const item = cart.find((c) => c.tagno === tagno);
      if (item) {
        setCartItemFormData((prev) => ({
          ...prev,
          [tagno]: {
            selectedItemPurityId: item.selectedItemPurityId || "",
            selectedItemPurityName: item.selectedItemPurityName || "",
            selectedItemRemark: item.selectedItemRemark || "",
            selectedItemPcs: item.selectedItemPcs || 1,
          },
        }));
      }
    }
  };

  // Make edit button disabled
  const isEditDisabled = (tagno, item) => {
    const formData = cartItemFormData[tagno] || {};

    const original = {
      // selectedItemRemark: item.selectedItemRemark || "",
      selectedItemPurityId: item.selectedItemPurityId || "",
      selectedItemPcs: item.selectedItemPcs || 1,
    };

    const current = {
      // selectedItemRemark: formData.selectedItemRemark || "",
      selectedItemPurityId: formData.selectedItemPurityId || "",
      selectedItemPcs: formData.selectedItemPcs ?? 1,
    };

    const hasChanged =
      // current.selectedItemRemark !== original.selectedItemRemark ||
      current.selectedItemPurityId !== original.selectedItemPurityId ||
      current.selectedItemPcs !== original.selectedItemPcs;

    const isComplete =
      current.selectedItemPurityId &&
      // current.selectedItemRemark &&
      current.selectedItemPcs >= 1;

    return !(hasChanged && isComplete); // returns `true` when button should be disabled
  };

  // const handleEditCartOrder = async (cartItem) => {
  //   try {
  //     const tagno = cartItem.tagno;
  //     const formData = cartItemFormData[tagno] || {};

  //     const cartItemObj = cart.find((c) => c.tagno === tagno);

  //     const updatedCartItemObj = {
  //       ...cartItemObj,
  //       selectedItemPcs: formData.selectedItemPcs || 1,
  //       selectedItemPurityId: formData.selectedItemPurityId || "",
  //       selectedItemPurityName: formData.selectedItemPurityName || "",
  //       selectedItemRemark: formData.selectedItemRemark || "",
  //     };
  //     console.log(updatedCartItemObj);

  //     const updatedCart = cart.map((item) =>
  //       item.tagno === tagno ? updatedCartItemObj : item
  //     );

  //     setCart(updatedCart);
  //     console.log(cart);

  //     // Optionally clear the form data for that tagno after saving
  //     // setCartItemFormData((prev) => {
  //     //   const newState = { ...prev };
  //     //   delete newState[tagno];
  //     //   return newState;
  //     // });

  //   } catch (error) {
  //     console.error("Error editing cart item:", error);
  //   }
  // };

  const handleEditCartOrder = async (cartItem, formData) => {
    try {
      const tagno = cartItem.tagno;
      const cartItemObj = cart.find((c) => c.tagno === tagno);

      const updatedCartItemObj = {
        ...cartItemObj,
        selectedItemPcs: formData.selectedItemPcs || 1,
        selectedItemPurityId: formData.selectedItemPurityId || "",
        selectedItemPurityName: formData.selectedItemPurityName || "",
        selectedItemRemark: formData.selectedItemRemark || "",
      };

      const updatedCart = cart.map((item) =>
        item.tagno === tagno ? updatedCartItemObj : item
      );

      setCart(updatedCart);

      // Optionally collapse after edit:
      setExpandedTagno(null);
    } catch (error) {
      console.error("Error editing cart item:", error);
    }
  };

  return (
    <Box>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          boxShadow: 1,
          mb: 0,
          height: isMobile ? 60 : 70,
          px: isMobile ? 2 : 5,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
            <Box
              height={isMobile ? 45 : 60}
              width={isMobile ? 45 : 60}
              sx={{
                backgroundImage: `url(${storeInformation.storeLogo})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "50%",
                backgroundColor: "#f5f5f5",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {" "}
              <img
                style={{
                  height: isMobile ? "60px" : "90px",
                  marginLeft: "8px",
                }}
                src={storeInformation.storeLogo}
              />{" "}
            </Box>
            {!isMobile && (
              <Typography sx={{ color: "red" }} variant="h6" fontWeight={600}>
                {storeInformation.storeName}{" "}
                {storeInformation.storePlace
                  ? `, ${storeInformation.storePlace}`
                  : ""}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setIsCartOpen(true)} size="large">
            <Badge badgeContent={cart.length} color="warning">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* 🔹 Mobile Filter Button */}
      {isMobile && (
        <Box display="flex" mb={1} mt={1} px={1}>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={() => setIsFilterDrawerOpen(true)}
            sx={{
              textTransform: "none",
              bgcolor: "#ba3800ff",
              "&:hover": { bgcolor: "#a33100" },
            }}
          >
            Filters
          </Button>
        </Box>
      )}

      <Grid
        container
        spacing={isMobile ? 0 : 1}
        p={isMobile ? 1 : 1}
        sx={{
          height: isMobile ? "auto" : "calc(100vh - 80px)",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Left Column — Sticky Filter Section */}
        <Grid
          item
          xs={12}
          md={2.3}
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "calc(100% + 24px)",
            borderRight: "2px solid #ba380011",
          }}
        >
          <Paper
            elevation={1}
            sx={{
              boxShadow: 0,
              bgcolor: "#fff",
              p: 2,
              borderRadius: 2,
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: 15,
                color: "#ba3800ff",
                mb: 1,
              }}
            >
              Filter Stocks
            </Typography>

            <Grid container spacing={2}>
              {/* Item Type */}
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  options={itemTypeOptions}
                  getOptionLabel={(o) => o.name}
                  value={
                    filters.itemType
                      ? itemTypeOptions.find(
                          (t) => t.id === filters.itemType
                        ) || null
                      : null
                  }
                  onChange={(e, v) => handleDropdownChange("itemType", v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Item Type" size="small" />
                  )}
                />
              </Grid>

              {/* Design Type */}
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  options={designOptions}
                  getOptionLabel={(o) => o.name}
                  value={
                    filters.design
                      ? designOptions.find((d) => d.id === filters.design) ||
                        null
                      : null
                  }
                  onChange={(e, v) => handleDropdownChange("design", v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Design Type" size="small" />
                  )}
                  disabled={!filters.itemType}
                />
              </Grid>

              {/* Weight fields */}
              {[
                {
                  label: "Gross From",
                  name: "grossWtMin",
                  value: filters.grossWtMin,
                },
                {
                  label: "Gross To",
                  name: "grossWtMax",
                  value: filters.grossWtMax,
                },
                {
                  label: "Net From",
                  name: "netWtMin",
                  value: filters.netWtMin,
                },
                { label: "Net To", name: "netWtMax", value: filters.netWtMax },
              ].map(({ label, name, value }) => (
                <Grid item xs={6} key={name}>
                  <TextField
                    label={label}
                    name={name}
                    type="number"
                    size="small"
                    fullWidth
                    value={value}
                    onChange={handleChange}
                  />
                </Grid>
              ))}

              {/* Filter Buttons */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={handleFilter}
                    disabled={!shouldFilterBeEnabled()}
                    sx={{
                      textTransform: "none",
                      flex: 1,
                      bgcolor: "#FFF2DB",
                      color: "#ba3800ff",
                      fontWeight: "bold",
                    }}
                  >
                    Filter
                  </Button>
                  <Collapse in={isFilterActive} orientation="horizontal">
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={handleClearFilters}
                      sx={{ textTransform: "none", flex: 1 }}
                    >
                      Clear
                    </Button>
                  </Collapse>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column — Scrollable Stock Section */}
        <Grid
          item
          xs={12}
          md={9}
          sx={{
            height: isMobile ? "auto" : "calc(100vh - 80px)",
            overflowY: isMobile ? "visible" : "auto",
            pr: isMobile ? 0 : 1,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box
            display="grid"
            gridTemplateColumns={
              isMobile ? "1fr 1fr" : "repeat(auto-fill, 264px)"
            }
            justifyContent={isMobile ? "center" : "space-between"}
            gap={isMobile ? 1 : 2}
            sx={{ mt: 1 }}
          >
            {paginatedStock.length === 0 ? (
              <Box
                sx={{ textAlign: "center", color: "gray", fontSize: "18px" }}
              >
                No Stock to show...
              </Box>
            ) : (
              paginatedStock.map((stock, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    mt: 1,
                  }}
                >
                  {/* Image */}
                  <Box
                    sx={{
                      height: isMobile ? "200px" : "244px",
                      borderRadius: 3,
                    }}
                    onClick={() =>
                      setSelectedImage(
                        `${IMAGE_BASE_URL}${
                          stock.tagno_alpha || stock.tagno
                        }.jpg`
                      )
                    }
                  >
                    <CardMedia
                      component="img"
                      image={`${IMAGE_BASE_URL}${
                        stock.tagno_alpha || stock.tagno
                      }.jpg`}
                      alt={stock.itemtype_name || "Stock Image"}
                      onError={(e) => (e.target.style.display = "none")}
                      sx={{
                        borderRadius: 3,
                        height: isMobile ? "186PX" : "236px",
                        width: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "scale(1.02)" },
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box
                    sx={{
                      // px: 1.5,
                      pt: 0.4,
                      height: "84px",
                      display: "flex",
                      flexDirection: "column",

                      // gap: 0.3,
                      // bgcolor: "red",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        // bgcolor: "yellow",
                      }}
                    >
                      {" "}
                      <Box>
                        {" "}
                        {stock.tagno && (
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              // py: 1,
                              borderRadius: 50,
                              width: "100%",
                              // textAlign: "center",
                              fontSize: "0.8rem",
                              // backgroundColor: "#FFF2DB",
                              color: "#ba3800ff",
                              // mb: 1,
                            }}
                          >
                            #{stock.tagno}
                          </Typography>
                        )}
                        {stock.itemtype_name && (
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{
                              // py: 1,
                              borderRadius: 50,
                              width: "100%",
                              // textAlign: "center",
                              fontSize: "0.7rem",
                              // backgroundColor: "#FFF2DB",
                              // color: "#ba3800ff",
                              // mb: 1,
                            }}
                          >
                            {stock.itemtype_name}
                          </Typography>
                        )}
                        {/* Other Stock Info */}
                        {stock.gross && (
                          <Typography
                            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                          >
                            Gross: {parseFloat(stock.gross).toFixed(3)}g
                          </Typography>
                        )}
                        {stock.netwt && store_id !== 13 && (
                          <Typography
                            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                          >
                            Net: {parseFloat(stock.netwt).toFixed(3)}g
                          </Typography>
                        )}
                      </Box>
                      {/* Button */}
                      <Box sx={{ px: 1 }}>
                        <Button
                          variant={
                            cart.some((item) => item.tagno === stock.tagno)
                              ? ""
                              : "contained"
                          }
                          color={
                            cart.some((item) => item.tagno === stock.tagno)
                              ? "#995c00ff"
                              : undefined
                          }
                          size="small"
                          onClick={() =>
                            cart.some((item) => item.tagno === stock.tagno)
                              ? removeFromCart(stock.tagno)
                              : addToCart(stock)
                          }
                          sx={{
                            minWidth: 46,
                            width: 46,
                            height: 46,

                            border: `1px solid #995c004e`,
                            borderRadius: "50%",
                            backgroundColor: !cart.some(
                              (item) => item.tagno === stock.tagno
                            )
                              ? "#FFF2DB"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: !cart.some(
                                (item) => item.tagno === stock.tagno
                              )
                                ? "#ffe0b3"
                                : "rgba(0,0,0,0.05)",
                            },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 0,
                          }}
                        >
                          {cart.some((item) => item.tagno === stock.tagno) ? (
                            "−"
                          ) : (
                            <ShoppingCartOutlinedIcon
                              sx={{ fontSize: 20, color: "#a33c00ff" }}
                            />
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" my={4}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              size="large"
              shape="rounded"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Cart drawer */}
      <Drawer
        anchor="right"
        open={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : 750,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* 👇 Header with Close Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              textAlign="center"
            >
              Cart Items
            </Typography>
            <IconButton onClick={() => setIsCartOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* 🧺 Cart Items */}
          <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
            <List dense>
              {cart.length === 0 && (
                <Box
                  sx={{
                    mt: 2,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                    Your cart is empty.
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                    Go ahead and add items.
                  </Typography>
                </Box>
              )}

              {cart.map((item, index) => {
                const formData = cartItemFormData[item.tagno] || {};
                return (
                  <ListItem
                    key={index}
                    sx={{
                      borderRadius: 2,
                      mb: 2,
                      px: isMobile ? 1.5 : 2,
                      py: 2,
                      boxShadow: 1,
                      bgcolor: "#f9f9f9",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "stretch",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    {/* LEFT SIDE: Image + Info */}
                    <Box
                      sx={{
                        flex: 2,
                        display: "flex",
                        flexDirection: isMobile ? "column" : "column",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                          gap: 2,
                          alignItems: isMobile ? "center" : "flex-start",
                        }}
                      >
                        <Avatar
                          src={`${IMAGE_BASE_URL}${
                            item.tagno_alpha || item.tagno
                          }.jpg`}
                          variant="rounded"
                          sx={{
                            width: isMobile ? 140 : 175,
                            height: isMobile ? 140 : 175,
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {item.itemtype_name || " "} (Tag:{" "}
                            {item.tagno_alpha || item.tagno})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Gross Wt: {parseFloat(item.gross).toFixed(3)} g
                          </Typography>
                          {store_id !== 13 && (
                            <Typography variant="body2" color="text.secondary">
                              Net Wt: {parseFloat(item.netwt).toFixed(3)} g
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Remove Button */}
                      <Button
                        onClick={() => removeFromCart(item.tagno)}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          alignSelf: isMobile ? "center" : "flex-start",
                          mt: isMobile ? 1.5 : 0,
                        }}
                        startIcon={<DeleteRoundedIcon />}
                      >
                        Remove from Cart
                      </Button>
                    </Box>

                    {/* RIGHT SIDE: Edit Fields + Edit Order */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <TextField
                          label="Remark"
                          size="small"
                          value={formData.selectedItemRemark || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              item.tagno,
                              "selectedItemRemark",
                              e.target.value
                            )
                          }
                        />

                        <Autocomplete
                          options={mapOptions(purities)}
                          getOptionLabel={(option) => option?.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            formData.selectedItemPurityId
                              ? {
                                  id: formData.selectedItemPurityId,
                                  name:
                                    purities.find(
                                      (p) =>
                                        p.id === formData.selectedItemPurityId
                                    )?.name || "",
                                }
                              : null
                          }
                          onChange={(event, newValue) =>
                            handleFieldChange(item.tagno, {
                              selectedItemPurityId: newValue?.id || "",
                              selectedItemPurityName: newValue?.name || "",
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Purity"
                              size="small"
                            />
                          )}
                        />

                        {/* PCS Counter */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #C0C0C0",
                            borderRadius: 1,
                            height: 40,
                            px: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Typography sx={{ mr: 1 }}>Pcs:</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleItemDecrement(item.tagno)}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <Typography
                            sx={{ mx: 1, minWidth: 20, textAlign: "center" }}
                          >
                            {formData.selectedItemPcs || 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleItemIncrement(item.tagno)}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Edit Order Button */}
                      <Button
                        disabled={isEditDisabled(item.tagno, item)}
                        variant="contained"
                        color="primary"
                        sx={{ fontWeight: "bold", mt: 2 }}
                        onClick={() => handleEditCartOrder(item, formData)}
                      >
                        Edit Order
                      </Button>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {/* Totals */}
          {store_id !== 13 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="flex-start"
              flexDirection="column"
              width="100%"
              mb={2}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="#0D7377"
                textAlign="center"
              >
                <span style={{ color: "black" }}>Total Net Wt:</span>{" "}
                {getTotalNetWeight()} g
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="#0D7377"
                textAlign="center"
              >
                <span style={{ color: "black" }}>Total Gross Wt:</span>{" "}
                {getTotalGrossWeight()} g
              </Typography>
            </Box>
          )}

          {/* Checkout */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setIsCheckoutOpen(true)}
            disabled={cart.length === 0}
            sx={{
              fontWeight: "bold",
              py: 1.2,
              borderRadius: 2,
            }}
          >
            Checkout
          </Button>
        </Box>
      </Drawer>

      {/* Mobile Filter */}
      <Drawer
        anchor="left"
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "80%",
            maxWidth: 380, // optional limit
            bgcolor: "#fff",
          },
        }}
      >
        {/* Header with Close Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #ddd",
            bgcolor: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: "#ba3800ff" }}
          >
            Filter Stocks
          </Typography>
          <IconButton onClick={() => setIsFilterDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        {/* 🔹 Filter Content — same as your existing sidebar content */}
        <Box sx={{ p: 2, overflowY: "auto", height: "calc(100vh - 70px)" }}>
          <Grid container spacing={2}>
            {/* Item Type */}
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                options={itemTypeOptions}
                getOptionLabel={(o) => o.name}
                value={
                  filters.itemType
                    ? itemTypeOptions.find((t) => t.id === filters.itemType) ||
                      null
                    : null
                }
                onChange={(e, v) => handleDropdownChange("itemType", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item Type"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Design Type */}
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                options={designOptions}
                getOptionLabel={(o) => o.name}
                value={
                  filters.design
                    ? designOptions.find((d) => d.id === filters.design) || null
                    : null
                }
                onChange={(e, v) => handleDropdownChange("design", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Design Type"
                    size="small"
                    fullWidth
                  />
                )}
                disabled={!filters.itemType}
              />
            </Grid>

            {/* Weight Fields */}
            {[
              {
                label: "Gross From",
                name: "grossWtMin",
                value: filters.grossWtMin,
              },
              {
                label: "Gross To",
                name: "grossWtMax",
                value: filters.grossWtMax,
              },
              { label: "Net From", name: "netWtMin", value: filters.netWtMin },
              { label: "Net To", name: "netWtMax", value: filters.netWtMax },
            ].map(({ label, name, value }) => (
              <Grid item xs={6} key={name}>
                <TextField
                  label={label}
                  name={name}
                  type="number"
                  size="small"
                  fullWidth
                  value={value}
                  onChange={handleChange}
                />
              </Grid>
            ))}

            {/* Filter Buttons */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                  mt: 1,
                  ml: 0.5,
                }}
              >
                {/* Filter Button */}
                <Button
                  size="small"
                  onClick={() => {
                    handleFilter();
                    setIsFilterDrawerOpen(false);
                  }}
                  disabled={!shouldFilterBeEnabled()}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#FFF2DB",
                    color: "#ba3800ff",
                    fontWeight: "bold",
                    px: 2.5,
                    borderRadius: 2,
                    "&:hover": { bgcolor: "#FFE8B8" },
                    boxShadow: "0 2px 6px rgba(186,56,0,0.2)",
                  }}
                >
                  Apply Filter
                </Button>

                {/* Clear Button (Only when active) */}
                <Collapse in={isFilterActive} orientation="horizontal">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleClearFilters}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      px: 2.5,
                      borderColor: "#ba3800ff",
                      color: "#ba3800ff",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#a33100",
                        bgcolor: "#FFF2DB",
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Collapse>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            aria-label="close"
            onClick={() => setSelectedImage(null)}
            sx={{ position: "absolute", top: 8, right: 8, color: "black" }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Stock Preview"
            style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)}>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            margin="dense"
            onChange={(e) =>
              setCustomerDetails({ ...customerDetails, name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Email"
            margin="dense"
            onChange={(e) =>
              setCustomerDetails({ ...customerDetails, email: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Phone"
            margin="dense"
            onChange={(e) =>
              setCustomerDetails({ ...customerDetails, phone: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Delivery Date"
            type="date"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={customerDetails.deliveryDate}
            onChange={(e) =>
              setCustomerDetails({
                ...customerDetails,
                deliveryDate: e.target.value,
              })
            }
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCheckoutOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendEmail}
            disabled={isSendingEmail}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loader while sending email */}
      <Dialog open={isSendingEmail}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
            gap: 2,
            textAlign: "center",
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            {currentLoadingMessage}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Success email sent notification */}
      <Dialog
        open={emailSuccessOpen}
        onClose={() => setEmailSuccessOpen(false)}
      >
        <DialogTitle>Email Sent</DialogTitle>
        <DialogContent>
          <Typography>Your order email has been successfully sent!</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailSuccessOpen(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockDataPage;
